import { useState, useEffect, useMemo } from "react";
import { FaShop } from "react-icons/fa6";
import { BsShop } from "react-icons/bs";
import { IoIosClose } from "react-icons/io";
import { FaPlay } from "react-icons/fa";
import { BsExclamationOctagonFill } from "react-icons/bs";
import Modal from "react-modal";
import { PlayerStatus, ProductModel } from "@/types";
import { useFullScreenHandle } from "react-full-screen";
import Player from "video.js/dist/types/player";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

export interface PlayReadyProps {
  id: string;
  open: boolean;
  onClose: (status: boolean) => void;
  player: Player | undefined;
  status: PlayerStatus;
  startAt: string;
  thumbnail: string;
  onStart?: () => void;
  privacyPolicyUrl: string;
  landscape?: boolean;
}

const PlayReadyModal = ({
  id,
  open = false,
  onClose,
  player,
  status,
  startAt,
  thumbnail,
  onStart,
  privacyPolicyUrl,
  landscape = false,
}: PlayReadyProps) => {
  const { t } = useTranslation();
  const [modalIsOpen, setIsOpen] = useState(open);

  // 日付をフォーマットする関数
  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      const currentLang = i18n.language;

      if (currentLang === "ja") {
        // 日本語フォーマット: YYYY年MM月DD日 HH:MM
        return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
      } else if (currentLang === "zh") {
        // 中国語フォーマット: YYYY年MM月DD日 HH:MM
        return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
      } else {
        // 英語フォーマット: MMM DD, YYYY HH:MM
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
      }
    } catch (e) {
      console.error("Date parsing error:", e);
      return dateString;
    }
  };

  // 言語に応じたテキストを取得
  const getScheduledText = () => {
    const formattedDate = formatDate(startAt);
    const lang = i18n.language;
    if (lang === "en") {
      return `Starting at ${formattedDate}`;
    } else if (lang === "zh") {
      return `${formattedDate} 开始`;
    } else {
      return `${formattedDate} 開始予定`;
    }
  };

  // 配信終了テキスト
  const getEndedText = () => {
    const lang = i18n.language;
    if (lang === "en") {
      return "Stream Ended";
    } else if (lang === "zh") {
      return "直播结束";
    } else {
      return "配信終了";
    }
  };

  // お礼テキスト
  const getThanksText = () => {
    const lang = i18n.language;
    if (lang === "en") {
      return "Thank you for watching!";
    } else if (lang === "zh") {
      return "感谢观看！";
    } else {
      return "ありがとうございました！";
    }
  };

  function closeModal() {
    setIsOpen(false);
    onClose(false);
  }

  const start = async () => {
    if (player) {
      await player.play();
      closeModal();
      onStart?.();
    }
  };

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  // Cleanup effect to ensure modal is closed when component unmounts
  useEffect(() => {
    return () => {
      if (modalIsOpen) {
        setIsOpen(false);
      }
    };
  }, []);

  const modalStyle = useMemo<Modal.Styles>(() => {
    const style: Modal.Styles = {
      overlay: {
        position: "absolute",
        inset: 0,
        backgroundColor: "transparent",
        zIndex: 20,
      },
      content: {
        left: "8%",
        right: "8%",
        top: "24%",
        bottom: "24%",
        padding: "0px",
        background: "transparent",
        border: "none",
        borderRadius: "6px",
      },
    };
    if (status === "ready" || status === "end") {
      return {
        ...style,
        content: {
          left: "0",
          right: "0",
          top: "0",
          bottom: "0",
          padding: "0",
          background: "rgba(0, 0, 0, 1)",
          border: "none",
        },
      };
    }
    return style;
  }, [status]);

  return (
    <Modal
      isOpen={modalIsOpen}
      contentLabel="Play Ready Modal"
      ariaHideApp={false}
      style={modalStyle}
      parentSelector={(): HTMLElement => {
        const element = document.getElementById(`play-ready-modal-${id}`);
        // Return document.body if the specific element is not found to prevent errors
        return element || document.body;
      }}
    >
      {status === "onAir" ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-w-full tw-h-full">
          <div className="tw-text-white tw-text-center">
            <button
              className="tw-rounded-full tw-bg-green-600/50 hover:tw-bg-green-500/50 tw-m-auto tw-p-5 tw-my-4 tw-border-none tw-outline-none tw-shadow-none tw-text-white"
              onClick={start}
            >
              <FaPlay className={`tw-inline-block tw-text-white tw-text-xl`} />
            </button>
            {privacyPolicyUrl && (
              <div className="tw-mt-4">
                <a
                  className="tw-text-xs tw-no-underline hover:tw-underline tw-text-white tw-drop-shadow"
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {String(t("player.privacy_policy"))}
                </a>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="tw-w-full tw-h-full tw-overflow-hidden tw-absolute tw-inset-0">
          {thumbnail && (
            <img
              src={thumbnail}
              className={`tw-w-full tw-h-full ${landscape ? "tw-object-contain" : "tw-object-cover"}`}
              alt="thumbnail"
            />
          )}
          {status === "ready" ? (
            startAt ? (
              <div className="tw-text-white tw-text-xs md:tw-text-sm tw-absolute tw-py-2 tw-px-4 tw-bg-black/50 tw-bottom-5 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-rounded-lg tw-text-center tw-font-medium tw-shadow-lg tw-whitespace-nowrap">
                {getScheduledText()}
              </div>
            ) : null
          ) : (
            <div className="tw-absolute tw-bottom-5 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-text-center tw-bg-black/50 tw-py-2 tw-px-5 tw-rounded-xl tw-shadow-lg">
              <div className="tw-flex tw-items-center tw-justify-center tw-mb-1">
                <BsExclamationOctagonFill className="tw-text-red-500 tw-text-xl tw-mr-2" />
                <span className="tw-text-white tw-font-bold tw-text-sm">
                  {getEndedText()}
                </span>
              </div>
              <p className="tw-text-white tw-text-xs tw-mt-0.5 tw-whitespace-nowrap">
                {getThanksText()}
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default PlayReadyModal;
